/**
 * ChatbotBuilder - Create custom AI chatbots from documents
 * 
 * Features:
 * - Upload PDF/TXT documents
 * - Create AI chatbot trained on your data
 * - Get embed code for your website
 * - Share link to chatbot
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Upload, Bot, FileText, Loader2, CheckCircle, 
  AlertCircle, Sparkles, Code, Link, ArrowRight 
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatbotBuilder() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    const validExtensions = ['.pdf', '.txt', '.docx', '.md', '.csv', '.json'];
    
    const hasValidExtension = validExtensions.some(ext => 
      selectedFile.name.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExtension) {
      setError('Please upload a supported file: PDF, TXT, DOCX, MD, CSV, or JSON');
      return;
    }
    
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File too large. Max 10MB');
      return;
    }
    
    setFile(selectedFile);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/auth?redirect=/chatbot-builder');
      return;
    }
    
    if (!name.trim()) {
      setError('Please enter a name for your chatbot');
      return;
    }
    
    if (!file) {
      setError('Please upload a document');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('file', file);
      
      const response = await axios.post(`${API}/chatbots/create`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create chatbot');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/50 border border-green-500/30 rounded-2xl p-8 text-center"
          >
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              Chatbot Created!
            </h1>
            <p className="text-zinc-400 mb-8">
              Your AI chatbot "{success.name}" is ready with {success.chunks_created} knowledge chunks.
            </p>
            
            <div className="space-y-4 text-left">
              {/* Share Link */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-teal-400 mb-2">
                  <Link className="w-4 h-4" />
                  <span className="font-medium">Share Link</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/chat/${success.public_id}`}
                    className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/chat/${success.public_id}`);
                    }}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    Copy
                  </Button>
                </div>
              </div>
              
              {/* Embed Code */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-blue-400 mb-2">
                  <Code className="w-4 h-4" />
                  <span className="font-medium">Embed on Your Website</span>
                </div>
                <div className="bg-zinc-900 rounded-lg p-3 font-mono text-xs text-zinc-300 overflow-x-auto">
                  {`<iframe src="${window.location.origin}/api/chatbots/embed/${success.public_id}" width="400" height="600" frameborder="0"></iframe>`}
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`<iframe src="${window.location.origin}/api/chatbots/embed/${success.public_id}" width="400" height="600" frameborder="0"></iframe>`);
                  }}
                  variant="outline"
                  className="mt-2 border-zinc-700 text-zinc-300"
                >
                  Copy Embed Code
                </Button>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <Button
                onClick={() => navigate(`/chat/${success.public_id}`)}
                className="flex-1 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-400 hover:to-blue-400"
              >
                Try Your Chatbot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/my-chatbots')}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                View All Chatbots
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Bot className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Create Your AI Chatbot
          </h1>
          <p className="text-zinc-400 text-lg">
            Upload a document and get an AI chatbot that answers questions about it.
            <br />
            <span className="text-teal-400">Perfect for FAQs, support docs, or knowledge bases.</span>
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          {/* Name */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Chatbot Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Product Support Bot"
              className="bg-zinc-800 border-zinc-700 text-white h-12"
              data-testid="chatbot-name-input"
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Description <span className="text-zinc-500">(optional)</span>
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this chatbot for? This helps the AI respond better."
              className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
              data-testid="chatbot-description-input"
            />
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">
              Upload Document *
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                dragActive 
                  ? 'border-teal-500 bg-teal-500/10' 
                  : file 
                    ? 'border-green-500/50 bg-green-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-zinc-500 text-sm">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setFile(null)}
                    className="text-zinc-400 hover:text-white"
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-4" />
                  <p className="text-zinc-300 mb-2">
                    Drag & drop your file here, or
                  </p>
                  <label className="cursor-pointer">
                    <span className="text-teal-400 hover:text-teal-300">
                      browse to upload
                    </span>
                    <input
                      type="file"
                      onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
                      className="hidden"
                      data-testid="chatbot-file-input"
                    />
                  </label>
                  <p className="text-zinc-500 text-sm mt-3">
                    PDF, TXT, DOCX, MD, CSV, JSON (max 10MB)
                  </p>
                </>
              )}
            </div>
            
            {/* File Type Guide */}
            <div className="mt-3 text-xs text-zinc-500 space-y-1">
              <p><span className="text-teal-400">.txt</span> — Fastest processing, simple text</p>
              <p><span className="text-teal-400">.pdf</span> — Reports, manuals, formatted docs</p>
              <p><span className="text-teal-400">.docx</span> — Word documents</p>
              <p><span className="text-teal-400">.csv</span> — Spreadsheets, data tables</p>
              <p><span className="text-zinc-400 italic">Tip: If a file fails, try copy-pasting content into a .txt file</span></p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-400 mb-6 bg-red-500/10 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || !name || !file}
            className="w-full h-12 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-400 hover:to-blue-400 text-white font-semibold rounded-xl"
            data-testid="create-chatbot-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating chatbot... (this may take a minute)
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Create Chatbot
              </>
            )}
          </Button>

          {/* Free tier notice */}
          {!user?.is_subscriber && (
            <p className="text-center text-zinc-500 text-sm mt-4">
              Free users can create 1 chatbot.{' '}
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="text-teal-400 hover:text-teal-300"
              >
                Upgrade for unlimited
              </button>
            </p>
          )}
        </motion.form>
      </div>
    </div>
  );
}
